import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown } from "lucide-react";
import { SourceSelect } from "@/components/common/SourceSelect";

/** Filter layout sections by the record's live context (conditional applicability + visibility). */
export function applicableSections(sections = [], ctx = {}) {
  const { hasOpportunity = false, candidateType = null, classifications = [], canRecruiting = true } = ctx;
  return [...sections]
    .filter((s) => s.visible !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .filter((s) => {
      if (s.render === "relationships" && !canRecruiting) return false;
      if ((s.render === "recruiting_summary" || s.render === "important_dates") && !canRecruiting) return false;
      const c = s.conditional || {};
      if (c.require_opportunity && !hasOpportunity) return false;
      if (Array.isArray(c.candidate_types) && c.candidate_types.length) {
        if (!candidateType || !c.candidate_types.includes(candidateType)) return false;
      }
      if (Array.isArray(c.classifications) && c.classifications.length) {
        if (!classifications.some((cl) => c.classifications.includes(cl))) return false;
      }
      return true;
    });
}

function isEmpty(v) {
  return v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);
}

function fmt(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (Array.isArray(v)) return v.join(", ");
  return v;
}

function CustomInput({ def, value, onChange }) {
  const t = def?.type || "text";
  if (t === "textarea") return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1" />;
  if (t === "yesno" || t === "checkbox")
    return (
      <Select value={value ? "yes" : "no"} onValueChange={(v) => onChange(v === "yes")}>
        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white"><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
      </Select>
    );
  if (t === "dropdown")
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent className="bg-white">{(def.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  const inputType = t === "number" || t === "currency" ? "number" : t === "date" ? "date" : t === "datetime" ? "datetime-local" : "text";
  return <Input type={inputType} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1" />;
}

/** Controlled dropdown for a core field; a saved value outside the list stays visible but can't be newly chosen. */
function CoreSelect({ options, value, saved, onChange, testId }) {
  const legacy = saved && !options.some((o) => o.key === saved) ? saved : null;
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="mt-1" data-testid={testId}><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent className="bg-white">
        {legacy && <SelectItem value={legacy} disabled>{legacy} (no longer offered)</SelectItem>}
        {options.map((o) => <SelectItem key={o.key} value={o.key} data-testid={`${testId}-opt-${o.key}`}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function SectionCard({ section, children, testId }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between px-5 py-4">
        <div className="min-w-0">
          <h3 className="font-display font-semibold text-slate-900 text-sm">{section.title}</h3>
          {section.description && <p className="text-xs text-slate-400 mt-0.5">{section.description}</p>}
        </div>
        {section.collapsible !== false && (
          <button onClick={() => setOpen((o) => !o)} className="p-1 rounded hover:bg-slate-100 text-slate-400" data-testid={`${testId}-toggle`}>
            <ChevronDown className={cn("w-4 h-4 transition-transform", !open && "-rotate-90")} />
          </button>
        )}
      </div>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

function FieldsSection({ section, record, custom, form, setForm, customDefs, orgs, edit, hideEmpty, coreOptions }) {
  const fields = (section.fields || []).filter((f) => f.visible !== false).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  if (!fields.length) return null;

  const getValue = (f) => {
    if (f.source === "custom") return (custom || {})[f.key];
    if (f.key === "organization_id") return record.organization_name;
    return record[f.key];
  };
  const setValue = (f, val) => {
    if (f.source === "custom") setForm({ ...form, custom_fields: { ...(form.custom_fields || {}), [f.key]: val } });
    else setForm({ ...form, [f.key]: val });
  };

  const visible = edit ? fields : fields.filter((f) => !(hideEmpty && isEmpty(getValue(f))));
  if (!visible.length) return null;

  const cols = section.columns === 1 ? "sm:grid-cols-1" : "sm:grid-cols-2";
  return (
    <div className={cn("grid grid-cols-1 gap-x-6 gap-y-4", cols)}>
      {visible.map((f) => {
        const editable = edit && !f.read_only && f.source !== "derived";
        const full = f.width === "full";
        return (
          <div key={`${f.source}:${f.key}`} className={cn(full && "sm:col-span-2")} data-testid={`field-${f.key}`}>
            <Label className="text-xs font-semibold text-slate-500">
              {f.label}{f.required && <span className="text-rose-500"> *</span>}
            </Label>
            {editable ? (
              f.key === "organization_id" ? (
                <Select value={form.organization_id || ""} onValueChange={(v) => setForm({ ...form, organization_id: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent className="bg-white">{(orgs || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : f.key === "source" ? (
                <SourceSelect organizationId={form.organization_id || record.organization_id} value={form.source_id} label={form.source}
                  testId="field-source-select" className="mt-1" onChange={({ source_id, source }) => setForm({ ...form, source_id, source })} />
              ) : coreOptions?.[f.key] ? (
                <CoreSelect options={coreOptions[f.key]} value={form[f.key]} saved={record[f.key]} testId={`field-${f.key}-select`} onChange={(v) => setValue(f, v)} />
              ) : f.source === "custom" ? (
                <CustomInput def={customDefs?.[f.key] || { type: f.type }} value={(form.custom_fields || {})[f.key]} onChange={(v) => setValue(f, v)} />
              ) : f.type === "textarea" ? (
                <Textarea value={form[f.key] || ""} onChange={(e) => setValue(f, e.target.value)} className="mt-1" />
              ) : (
                <Input value={form[f.key] || ""} onChange={(e) => setValue(f, e.target.value)} className="mt-1" />
              )
            ) : (
              <p className="text-sm text-slate-800 mt-1 break-words">{isEmpty(getValue(f)) ? "—" : (coreOptions?.[f.key]?.find((o) => o.key === getValue(f))?.label || fmt(getValue(f)))}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Renders a set of layout sections. "fields" sections are generic (core + custom);
 * system render types (recruiting_summary / important_dates / agent_profile / relationships)
 * are delegated to `systemRenderers[render](section)` provided by the page.
 */
export function RecordSections({ sections, record, custom, form, setForm, customDefs, orgs, edit = false, hideEmpty = false, systemRenderers = {}, coreOptions }) {
  return (
    <div className="space-y-6" data-testid="record-sections">
      {sections.map((section) => {
        const testId = `section-${section.key}`;
        if (section.render && section.render !== "fields") {
          const node = systemRenderers[section.render]?.(section);
          if (!node) return null;
          return <SectionCard key={section.key} section={section} testId={testId}>{node}</SectionCard>;
        }
        const body = (
          <FieldsSection section={section} record={record} custom={custom} form={form} setForm={setForm}
            customDefs={customDefs} orgs={orgs} edit={edit} hideEmpty={hideEmpty} coreOptions={coreOptions} />
        );
        // Hide fully-empty fields sections entirely (avoids nonfunctional clutter).
        const hasFields = (section.fields || []).some((f) => f.visible !== false);
        if (!hasFields && !edit) return null;
        return <SectionCard key={section.key} section={section} testId={testId}>{body || <p className="text-sm text-slate-400">No fields configured.</p>}</SectionCard>;
      })}
    </div>
  );
}

/** Simple key/value grid used to render derived (read-only) system sections. */
export function DerivedGrid({ rows, columns = 2, testId }) {
  const shown = rows.filter((r) => !isEmpty(r[1]));
  if (!shown.length) return <p className="text-sm text-slate-400">No information yet.</p>;
  return (
    <div className={cn("grid grid-cols-1 gap-x-6 gap-y-4", columns === 1 ? "sm:grid-cols-1" : "sm:grid-cols-2")} data-testid={testId}>
      {shown.map(([k, v], i) => (
        <div key={`${k}-${i}`}>
          <p className="text-xs font-semibold text-slate-500">{k}</p>
          <p className="text-sm text-slate-800 mt-0.5 break-words">{fmt(v)}</p>
        </div>
      ))}
    </div>
  );
}
